import axios from 'axios'
import { RELATED_PAGE_SIZE, RELATED_OPTIONS_TTL_MS, relatedPath } from '@/talkture/scenario/components/select/relatedOptions'

// Requisições idênticas (mesma lista, página e termo) em andamento: quem pede de novo recebe a mesma promessa em vez de disparar outra e
// cancelar a primeira (várias instâncias do mesmo campo montadas juntas — ex.: CarrierEdit, filtros — pediam N vezes e cancelavam N-1).
const inFlight = new Map()

// Um AbortController por campo (chave do cache sem o termo): abrir o combo de OUTRO campo não cancela mais a lista em voo do primeiro;
// continua cancelando a busca anterior do MESMO campo (digitação com debounce, rolagem, refresh).
const controllers = new Map()

const errorMessageOf = (error) => error?.response?.data?.errors?.[0]?.message

const moduleSubjectAttribute = {
  state: () => ({
    options: {},
    // Quando cada entrada de `options` foi gravada (ms). Usado só para expirar listas antigas (RELATED_OPTIONS_TTL_MS).
    optionsAt: {}
  }),
  getters: {
    getOptions (state) {
      return state.options
    }
  },
  mutations: {
    SET_OPTIONS (state, { identifier, data }) {
      state.options[identifier] = data
      state.optionsAt[identifier] = Date.now()
    },
    CLEAR_OPTIONS (state, identifier) {
      delete state.options[identifier]
      delete state.optionsAt[identifier]
    },
    // Limpa a lista base de um campo E todas as buscas por termo dela (chave/termo) — o refresh recarrega tudo, e não deve
    // sobrar uma busca antiga em cache.
    CLEAR_OPTIONS_TREE (state, identifier) {
      Object.keys(state.options)
        .filter((key) => key === identifier || key.startsWith(`${identifier}/`))
        .forEach((key) => {
          delete state.options[key]
          delete state.optionsAt[key]
        })
    },
    // Semeia uma lista que veio de outro lugar (os valores de uma lista fixa que o contexto da TalkModal já traz), com o instante em que
    // o DADO foi lido (não "agora"): assim o TTL conta a partir do contexto, e uma lista semeada de um contexto antigo não rejuvenesce.
    // Nunca sobrescreve uma lista que já está na store.
    SEED_OPTIONS (state, { identifier, data, at }) {
      if (state.options[identifier] || !at || Date.now() - at > RELATED_OPTIONS_TTL_MS) return

      state.options[identifier] = data
      state.optionsAt[identifier] = at
    },
    // Logout: as listas podem depender do usuário e da sala (Solicitante, Software), não podem sobrar para o próximo login.
    CLEAR_ALL_OPTIONS (state) {
      state.options = {}
      state.optionsAt = {}
    },
    // Remove as listas mais velhas que o TTL. Chamado antes de ler uma lista ao abrir o dropdown.
    EXPIRE_OPTIONS (state, now = Date.now()) {
      Object.keys(state.optionsAt)
        .filter((key) => now - state.optionsAt[key] > RELATED_OPTIONS_TTL_MS)
        .forEach((key) => {
          delete state.options[key]
          delete state.optionsAt[key]
        })
    }
  },
  actions: {
    // Achado real (2026-09-15): esta action sempre montou a URL a partir de `attrId`, ignorando por
    // completo o prop `api` que SelectEdit.vue já expõe pra reusos que não são "campo relacionado de um
    // Talk" (ex.: AccessFilter.vue, que lista Talks de um Subject arbitrário via `talk/{subject}`). Sem
    // `attrId`, isso virava `GET SubjectAttribute/related/undefined` (404).
    //
    // `attrId` continua aceito, por compatibilidade com SelectSearch.vue (o outro consumidor desta action,
    // via AttributeSearch.vue) — ele só conhece `attrId`, nunca `api`. Quando `api` vem preenchido (SelectEdit.vue,
    // que sempre o passa desde esta correção), ele é o caminho relativo completo e vence; sem `api`, cai no
    // comportamento de sempre.
    async loadSubjectAttributesList (
      { commit, getters, state, dispatch },
      { page, term, attrId, api, extraParams = '' }
    ) {
      // `path` é o caminho de rede real e também a base da chave de cache: com só `attrId` a chave passa a ser a mesma
      // que SelectEdit usa (`api`), então o filtro do grid e o formulário compartilham a lista em vez de buscá-la duas vezes.
      const path = api ?? relatedPath(attrId)
      const key = `${path}${extraParams}`
      const identifier = term ? `${key}/${term}` : key

      // Mesma lista, página e termo já em andamento: reaproveita a requisição (e não cancela a original, que é o que o abort abaixo faria).
      const flightKey = `${identifier}|${page}`
      if (inFlight.has(flightKey)) return inFlight.get(flightKey)

      controllers.get(key)?.abort()
      const controller = new AbortController()
      controllers.set(key, controller)

      const run = async () => {
        try {
          const { data } = await axios.get(`${getters.api}/${path}${extraParams}`, {
            params: {
              page,
              fetch: RELATED_PAGE_SIZE,
              term
            },
            signal: controller.signal
          })

          // Página seguinte de uma lista que foi limpada enquanto a requisição voava (refresh, expiração): descarta, em vez de
          // montar uma lista sem a primeira página.
          if (page > 1 && !state.options[identifier]) return

          commit('SET_OPTIONS', {
            identifier,
            data: {
              itens: page > 1 ? [...state.options[identifier].itens, ...data.itens] : data.itens,
              currentPage: page,
              totalResults: data.totalResults,
              ...(term ? { term } : {})
            }
          })
        } catch (error) {
          if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return

          const message = errorMessageOf(error)
          const status = error.response?.status ?? error.status

          if ([401, 404].includes(status) && message) {
            commit('SET_OPTIONS', { identifier, data: { error: message } })

            dispatch('notification', { type: 'error', text: message })
          } else {
            dispatch('notification', {
              type: 'error',
              text: 'Ocorreu um erro interno em nosso servidor, tente novamente mais tarde ou contate um de nossos administradores.'
            })
          }
        } finally {
          if (controllers.get(key) === controller) controllers.delete(key)
        }
      }

      const request = run().finally(() => inFlight.delete(flightKey))
      inFlight.set(flightKey, request)

      return request
    }
  }
}

export default moduleSubjectAttribute
